import React, { useContext, useState } from 'react';

import { ApiRoutes, FetchContext, HttpMethods, ToastContext } from '../../contexts';

import { useAggregatedState } from '../../../helpers';

export const ComuneCompetenzaContext = React.createContext(null);

const { Provider } = ComuneCompetenzaContext;

export const ComuneCompetenzaProvider = (props) => {
    const {
        showWarningMessage,
        showSuccessMessage,
        showErrorMessage
    } = useContext(ToastContext);
    const { fetchOk, fetchAuthorized } = useContext(FetchContext);
    const [state, setState] = useAggregatedState({
        ufficioComuneCompetenza: null,
        competenze: null,
        competenzeOptions: [],
        uffici: null,
        ufficiOptions: [],
        tipiUffici: null,
        eccezioni: null,
    });

    const { eccezioni } = state;

    const [isUpdatingUfficioComuneCompetenza, setIsUpdatingUfficioComuneCompetenza] = useState(false);
    const updateUfficioComuneCompetenzaAsync = async (comuneId, competenzaId, competenza) => {
        try {
            setIsUpdatingUfficioComuneCompetenza(true);
            const r = await fetchAuthorized(ApiRoutes.Comune.UpdateCompetenza({ comuneId, competenzaId }), {
                method: HttpMethods.POST,
                body: JSON.stringify(competenza),
            });
            const result = await r.json();
            setState({ ufficioComuneCompetenza: result });
            showSuccessMessage("Competenza aggiornata");
            window.close();
        } catch (e) {
            console.debug(e);
        } finally {
            setIsUpdatingUfficioComuneCompetenza(false);
        }
    }

    const [isLoadingTipiUffici, setIsLoadingTipiUffici] = useState(false);
    const getTipiUfficiAsync = async (pageIndex = 0, pageSize = 100) => {
        try {
            setIsLoadingTipiUffici(true);
            const r = await fetchAuthorized(
                ApiRoutes.TipoUfficio.GetTipiUffici({ pageIndex, pageSize }),
                {
                    method: HttpMethods.GET,
                }
            );
            const list = await r.json();
            list.result.forEach(x => x.dataCreazione = new Date(x.dataCreazione));
            setState({
                tipiUffici: list,
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingTipiUffici(false);
        }
    };

    const [isAddingUfficioComuneCompetenza, setIsAddingUfficioComuneCompetenza] = useState(false);
    const addUfficioComuneCompetenzaAsync = async (comuneId, competenzaId, competenza) => {
        try {
            setIsAddingUfficioComuneCompetenza(true);
            const r = await fetchAuthorized(ApiRoutes.Comune.AddCompetenza({ comuneId, competenzaId }), {
                method: HttpMethods.PUT,
                body: JSON.stringify(competenza),
            });
            const result = await r.json();
            setState({ ufficioComuneCompetenza: result });
            showSuccessMessage("Competenza creata");
            window.close();
        } catch (e) {
            console.debug(e);
        } finally {
            setIsAddingUfficioComuneCompetenza(false);
        }
    }

    const [isLoadingUfficioComuneCompetenza, setIsLoadingUfficioComuneCompetenza] = useState(false);
    const getUfficioComuneCompetenzaAsync = async (comuneId, competenzaId) => {
        try {
            setIsLoadingUfficioComuneCompetenza(true);
            const r = await fetchAuthorized(ApiRoutes.Comune.GetCompetenza({ comuneId, competenzaId }), {
                method: HttpMethods.GET
            });
            const result = await r.json();
            result.inizioValidita = new Date(result.inizioValidita);
            result.fineValidita = result.fineValidita ? new Date(result.fineValidita) : null;
            setState({ ufficioComuneCompetenza: result });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingUfficioComuneCompetenza(false);
        }
    }

    const [isLoadingCompetenze, setIsLoadingCompetenze] = useState(false);
    const getCompetenzeAsync = async (pageIndex = 0, pageSize = 100) => {
        try {
            setIsLoadingCompetenze(true);
            const r = await fetchAuthorized(ApiRoutes.Competenza.GetCompetenze({ pageIndex, pageSize }), {
                method: HttpMethods.GET
            });
            const list = await r.json();
            setState({
                competenze: list, competenzeOptions: list.result.map(e => {
                    return { competenzaId: e.id, label: `${e.nome}` }
                }).sort((a, b) => a.label.localeCompare(b.label))
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingCompetenze(false);
        }
    }

    const [isLoadingUffici, setIsLoadingUffici] = useState(false);
    const getUfficiAsync = async (tipiIds = null, pageIndex = 0, pageSize = 1000) => {
        try {
            setIsLoadingUffici(true);
            const r = await fetchAuthorized(ApiRoutes.Ufficio.GetUffici(), {
                method: HttpMethods.POST,
                body: JSON.stringify({ pageIndex, pageSize, tipiIds: tipiIds, }),
            });
            const list = await r.json();
            setState({
                uffici: list, ufficiOptions: list.result.map(e => {
                    return { ufficioId: e.id, label: `${e.nome}` }
                }).sort((a, b) => a.label.localeCompare(b.label))
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingUffici(false);
        }
    }

    const [isLoadingEccezioni, setIsLoadingEccezioni] = useState(false);
    const getEccezioniAsync = async (comuneId, competenzaId, pageIndex = 0, pageSize = 100,) => {
        try {
            setIsLoadingEccezioni(true);
            const r = await fetchAuthorized(ApiRoutes.Comune.GetEccezioni({
                pageIndex,
                pageSize,
                comuneId,
                competenzaId
            }), {
                method: HttpMethods.GET
            });
            const list = await r.json();
            list.result.forEach((x) => {
                x.inizioValidita = new Date(x.inizioValidita);
                x.fineValidita = x.fineValidita ? new Date(x.fineValidita) : null;
            });
            setState({ eccezioni: list });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingEccezioni(false);
        }
    }

    const [isAddingEccezione, setIsAddingEccezione] = useState(false);
    const addEccezioneAsync = async (comuneId, competenzaId, eccezione) => {
        try {
            setIsAddingEccezione(true);
            if (window.confirm("Confermare l'inserimento?")) {
                const r = await fetchAuthorized(ApiRoutes.Comune.AddEccezione({ comuneId, competenzaId }), {
                    method: HttpMethods.PUT,
                    body: JSON.stringify(eccezione),
                });
                const result = await r.json();
                setState({ eccezione: result, eccezioni: { ...eccezioni, result: [...eccezioni.result, result] } });
                showSuccessMessage("Eccezione creata")
            }
        } catch (e) {
            console.debug(e);
        } finally {
            setIsAddingEccezione(false);
        }
    }

    const [isDeletingEccezione, setIsDeletingEccezione] = useState(false);
    const deleteEccezioneAsync = async (comuneId, competenzaId, eccezioneId) => {
        try {
            setIsDeletingEccezione(true);
            if (window.confirm("Confermare l'eliminazione?")) {
                const r = await fetchAuthorized(ApiRoutes.Comune.DeleteEccezione({
                    comuneId,
                    competenzaId,
                    eccezioneId
                }), {
                    method: HttpMethods.DELETE
                });
                setState({ eccezioni: { ...eccezioni, result: eccezioni.result.filter(x => x.id != eccezioneId) } });
                showSuccessMessage(`Eccezione con id ${eccezioneId} eliminato`);
            }
        } catch (e) {
            console.debug(e);
        } finally {
            setIsDeletingEccezione(false);
        }
    }

    const [isLoadingStorici, setIsLoadingStorici] = useState(false);
    const getStoriciAsync = async (comuneId, competenzaId, pageIndex = 0, pageSize = 1000,) => {
        try {
            setIsLoadingStorici(true);
            const r = await fetchAuthorized(ApiRoutes.Comune.GetStorici({
                pageIndex,
                pageSize,
                comuneId,
                competenzaId
            }), {
                method: HttpMethods.GET
            });
            const list = await r.json();
            list.result = list.result.map(e => {
                return { ...e, dataCreazione: new Date(e.dataCreazione) }
            });
            setState({ storici: list, });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingStorici(false);
        }
    }

    return (
        <Provider value={{
            state,
            setState,

            isLoadingUfficioComuneCompetenza,
            getUfficioComuneCompetenzaAsync,

            isUpdatingUfficioComuneCompetenza,
            updateUfficioComuneCompetenzaAsync,

            isAddingUfficioComuneCompetenza,
            addUfficioComuneCompetenzaAsync,

            isLoadingCompetenze,
            getCompetenzeAsync,

            isLoadingUffici,
            getUfficiAsync,

            isLoadingEccezioni,
            getEccezioniAsync,

            isAddingEccezione,
            addEccezioneAsync,

            isDeletingEccezione,
            deleteEccezioneAsync,

            isLoadingTipiUffici,
            getTipiUfficiAsync,

            isLoadingStorici,
            getStoriciAsync,
        }}>
            {props.children}
        </Provider>
    );
}